import type { ApiError } from '@/lib/problem-details'

/**
 * Remediation for the savings and till failures that the shared table in `src/lib/error-messages.ts`
 * does not carry.
 *
 * That table is shared and generic; these are the codes a savings screen specifically has to turn
 * into an instruction, and several of them are the direct consequence of a mis-tap between two
 * things that look alike on the same screen. `savings.account_wrong_class` is the clearest case:
 * it is raised both by a deposit aimed at a security deposit account and by a top-up aimed at a
 * regular savings one, which are the two most likely wrong tabs to be on.
 *
 * The shared table is not edited from here. `resolveErrorMessage` already falls back to the
 * problem's own detail for a code it does not know, so the alert underneath still says something
 * true; this only adds the next step.
 */
const SAVINGS_GUIDANCE: Record<string, string> = {
  // The two classes, and the mis-tap between them
  'savings.account_wrong_class':
    'That operation does not apply to this class of account. A deposit goes to a regular savings account and a top-up to a security deposit account — check which tab you are on.',
  'savings.account_not_withdrawable':
    'This is a security deposit. It is collateral, not the customer’s spendable balance: it is released by a countersigned guarantee offset, or by closing the account.',

  // Lifecycle
  'savings.account_not_dormant':
    'This account is not dormant, so there is nothing to reactivate. The screen may have been open while somebody else acted on it — reload it.',
  'savings.account_not_settled':
    'The account cannot close while it is still settling. Clear what is outstanding first.',
  'savings.account_has_active_holds': 'Release every hold on this account before closing it.',
  'savings.account_already_closed': 'This account is already closed.',
  'savings.duplicate_account': 'This customer already holds an open account of that class.',

  // Holds
  'savings.hold_already_released':
    'That hold has already been released — by someone else, or by an earlier click. Reload the account to see the balances as they now stand.',
  'savings.hold_exceeds_balance':
    'A hold cannot exceed the unheld ledger balance. Re-posting a hold with a reason that already has an active one amends that hold rather than adding a second, so the ceiling includes what it already holds.',

  // Lifecycle, continued
  'savings.account_not_active':
    'This account is not open for transactions. Only an active account takes a posting — a dormant one has to be reactivated first, and a frozen or closed one cannot be posted to at all.',

  // What the form should have caught, and the bounds behind it
  'savings.search_too_long':
    'A search term is at most 32 characters. Search on the account number, the customer number, or part of the name rather than the whole of it.',
  'savings.narration_too_long':
    'A narration is at most 500 characters. It is a note against the posting, not the story behind it.',
  'savings.dormancy_days_out_of_range':
    'A dormancy period is a whole number of days between 0 and 3650 — ten years. Zero means the product never ages into dormancy.',
  'savings.passbook_number_too_long': 'That serial is longer than the register accepts.',
  'savings.statement_period_invalid':
    'The From date has to fall on or before the To date. Clear the range to read the whole history.',
  'savings.opened_date_in_future':
    'An account cannot have been opened on a date that has not happened. Use today, or the day the customer actually signed.',
  'savings.issued_date_in_future':
    'A book cannot be issued on a date that has not happened.',

  // Amounts, dates, currency
  'savings.amount_not_positive': 'An amount has to be greater than zero.',
  'savings.value_date_out_of_window':
    'A value date must be today or inside the institution’s backdating window, and never in the future.',
  'savings.currency_mismatch': 'The amount is in a different currency from the account.',
  'savings.minimum_opening_balance':
    'The first payment into this account is below the product’s minimum opening balance.',
  'savings.below_minimum_balance':
    'This would take the account below the product’s minimum balance. Leave the floor in place, or close the account instead.',

  // Passbooks
  'savings.passbook_number_taken':
    'That serial is already registered somewhere in the institution. Serials are unique across every branch, not only this one.',
  'savings.passbook_already_held':
    'This customer already holds an active book — possibly one issued at a branch you cannot see, which is why it may not appear in the list. Replace it rather than issuing a second.',
  'savings.passbook_not_replaceable': 'That book has already been replaced or retired.',

  // Products
  'savings.product_code_taken': 'That product code is already in use.',
  'savings.product_interest_never_posts':
    'A product paying interest needs a posting frequency. Either set a frequency, or set the rate to zero.',
  'savings.product_class_not_withdrawable':
    'A security deposit product cannot be withdrawable — the class and the withdrawability are one decision.',
  'savings.interest_rate_out_of_range':
    'The rate crosses the wire as a fraction between 0 and 1. This form sends a percentage divided by 100, so a rate above 100% is what produces this.',
  'savings.product_inactive': 'That product is retired and cannot take new accounts.',

  // Transfers
  'savings.transfer_same_account': 'A transfer needs two different accounts.',
  'savings.transfer_branch_mismatch':
    'Both accounts have to belong to a branch you can act for. Check the destination account number.',

  // The till, which savings postings run through and savings does not own
  'teller.no_open_till':
    'You have no open till. Open a session on a counter till, or be assigned a field till, before posting cash. Nothing was posted.',
  'teller.till_cash_limit_exceeded':
    'This would take your drawer over its cash holding limit. Bank cash to the vault, then post again.',
  'teller.till_insufficient_cash':
    'Your drawer does not hold that much cash. Draw from the vault before paying this out.',
  'teller.session_not_yours': 'A drawer belongs to one person, and that one is not yours.',
}

export const savingsErrorGuidance = (error: ApiError): string | null =>
  SAVINGS_GUIDANCE[error.code] ?? null
